import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { MasterItem, SearchWallpaper, buildPdpUrl } from '../../models/wallpaper.model';
import { SPACES, CATEGORIES, categoryIdFromToken, categorySlugById } from '../../models/constants';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-category-listing',
  templateUrl: './category-listing.component.html'
})
export class CategoryListingComponent implements OnInit, OnDestroy {
  // Category data
  category: any = null;
  categorySlug = '';
  categoryId: number | null = null;
  bannerImage = '';
  bannerImages: string[] = [];
  bannerIndex = 0;
  private bannerTimer: any;

  // Wallpaper data
  wallpapers: SearchWallpaper[] = [];
  isLoading = false;
  loadError = false;
  totalRecords = 0;

  // Spaces for circle filter
  spacesList: MasterItem[] = [];
  selectedSpaceId: number | null = null;

  // Pagination
  currentPage = 1;
  readonly pageSize = 12;

  // Sort
  sortBy = 'Recommended';
  sortOptions = ['Recommended', 'Popular', 'Newest', 'Price: Low \u2192 High', 'Price: High \u2192 Low'];
  showSortDropdown = false;

  // Description
  showFullDescription = false;

  // Sub-categories grid
  subCategories: any[] = [];

  // Filter drawer
  showFiltersMobile = false;
  selectedTypes = new Set<string>();
  allCategories: any[] = [];
  selectedCategoryIds = new Set<number>();
  selectedSpaceIds = new Set<number>();
  selectedColorIds = new Set<number>();
  selectedThemeIds = new Set<number>();
  selectedQualities = new Set<string>();
  vastu = false;
  masters: any = {};
  private expandedSections = new Set<string>(['m-category', 'm-space', 'm-type', 'm-quality', 'm-color', 'm-vastu']);

  // Wishlist
  wishlistIds = new Set<string>();

  private sub = new Subscription();
  private pageEnterTime = 0;
  private scrollMilestones = new Set<number>();
  private userActionCount = 0;
  private hesitationTimer: any;
  private exitIntentFired = false;

  // Static category banner images
  private readonly categoryImages: Record<string, string> = {
    'modern': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1921_low_1772048375129.jpg',
    'floral': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new1/SM-Nouv-0027_0.jpg',
    'florals': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new1/SM-Nouv-0027_0.jpg',
    'abstract': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1769_low_1772046872915.jpg',
    'abstract-geometrics': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1769_low_1772046872915.jpg',
    'scenic': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1828_low_1772047739973.jpg',
    'kids': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1740_low_1772046369981.jpg',
    'animals': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2218_low_1772056002469.jpg',
    'spiritual': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1999_low_1772050148050.jpg',
    'nature': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1730_low_1772046156689.jpg',
    'textures': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/SM_Gabb_0060_15x10_C1_C_low_1771220625329.jpg',
    'cities-maps': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-1332_low_1772043956324.jpg',
    'classic-vintage': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2344_low_1771589557696.jpg',
    'photographic': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2230_low_1772056296997.jpg',
    'middle-eastern': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2331_low_1771588840241.jpg',
    'east-asian': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2353_low_1771590194088.jpg',
    'indian-heritage': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2169_low_1772054582239.jpg',
    'south-east-asian': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/SM_Ikat_0054_35x35_C1_P_low_1770800894314.jpg',
    'silk-route': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2476_low_1771653899909.jpg',
    'art': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-C-2142_low_1772053262564.jpg',
    'textile': 'https://storage.googleapis.com/static-mahattaart-online/wallpaper_lowres_new_latest/MAWP-P-2254_low_1772056646839.jpg',
  };

  constructor(
    public state: AppStateService,
    public router: Router,
    private route: ActivatedRoute,
    private wallpaperService: WallpaperService,
    private location: Location,
    private dataLayer: DataLayerService,
  ) { }

  ngOnInit() {
    this.loadMasters();
    // Spaces are hardcoded — no API call needed
    this.spacesList = SPACES.map(s => ({
      id: s.id,
      name: s.display_name,
      display_name: s.display_name,
      slug: s.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    }));

    this.sub.add(
      this.route.paramMap.subscribe(params => {
        this.categorySlug = params.get('slug') || '';
      })
    );

    this.sub.add(
      this.route.queryParamMap.subscribe(qp => {
        // Accepts a readable slug (?category=florals) or a legacy numeric id
        // (?category=5) so links already in the wild keep working.
        const catToken = qp.get('category');
        if (catToken) {
          const resolved = categoryIdFromToken(catToken);
          if (resolved !== null) this.categoryId = resolved;
        }

        const sortby = qp.get('sortby');
        const sortdir = qp.get('sortdir');
        if (sortby === 'price' && sortdir === 'asc') this.sortBy = 'Price: Low \u2192 High';
        else if (sortby === 'price' && sortdir === 'desc') this.sortBy = 'Price: High \u2192 Low';
        else this.sortBy = 'Recommended';

        const page = qp.get('page');
        this.currentPage = page ? +page : 1;
        this.loadCategoryData();
        this.loadWallpapers();
      })
    );

    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistIds = ids));

    // B8/B9/B12 init
    this.pageEnterTime = Date.now();
    this.scrollMilestones = new Set();
    this.userActionCount = 0;
    this.exitIntentFired = false;
    this.startHesitationTimer();

    // Rotate banner every 5 seconds
    this.bannerTimer = setInterval(() => {
      if (this.bannerImages.length > 1) {
        this.bannerIndex = (this.bannerIndex + 1) % this.bannerImages.length;
      }
    }, 5000);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    if (this.bannerTimer) clearInterval(this.bannerTimer);
    clearTimeout(this.hesitationTimer);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);
    [25, 50, 75, 100].forEach(m => {
      if (pct >= m && !this.scrollMilestones.has(m)) {
        this.scrollMilestones.add(m);
        this.dataLayer.pushPlpScroll({ scrollPercent: m, timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), filtersActive: this.buildActiveFilters() });
      }
    });
    this.userActionCount++;
  }

  private startHesitationTimer(): void {
    clearTimeout(this.hesitationTimer);
    this.hesitationTimer = setTimeout(() => {
      if (this.userActionCount < 3) {
        this.dataLayer.pushUserHesitation({ timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), actionsTaken: this.userActionCount, filtersActive: this.buildActiveFilters() });
      }
    }, 90000);
  }

  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(e: MouseEvent): void {
    if (e.clientY <= 0 && !this.exitIntentFired) {
      this.exitIntentFired = true;
      this.dataLayer.pushExitIntent({ timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), filtersActive: this.buildActiveFilters(), itemsViewed: this.wallpapers.length });
    }
  }

  goBack() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    } else {
      this.router.navigate(['/wallpaper-home']);
    }
  }

  toggleSection(key: string) {
    if (this.expandedSections.has(key)) this.expandedSections.delete(key);
    else this.expandedSections.add(key);
  }
  isSectionExpanded(key: string): boolean { return this.expandedSections.has(key); }

  get masterSpaces(): MasterItem[] {
    const list = this.spacesList.length > 0 ? this.spacesList : (this.masters?.['space'] || []);
    return list.filter((s: any) => !(s.name || s.display_name || '').toLowerCase().includes('retail'));
  }
  private readonly fallbackColors: MasterItem[] = [
    { id: 1, name: 'Red', display_name: 'Red', slug: 'red', hex: '#DC2626' },
    { id: 2, name: 'Blue', display_name: 'Blue', slug: 'blue', hex: '#2563EB' },
    { id: 3, name: 'Green', display_name: 'Green', slug: 'green', hex: '#16A34A' },
    { id: 4, name: 'Yellow', display_name: 'Yellow', slug: 'yellow', hex: '#EAB308' },
    { id: 5, name: 'Orange', display_name: 'Orange', slug: 'orange', hex: '#EA580C' },
    { id: 7, name: 'Purple', display_name: 'Purple', slug: 'purple', hex: '#9333EA' },
    { id: 8, name: 'Brown', display_name: 'Brown', slug: 'brown', hex: '#92400E' },
    { id: 9, name: 'Black', display_name: 'Black', slug: 'black', hex: '#1F2937' },
    { id: 10, name: 'White', display_name: 'White', slug: 'white', hex: '#F9FAFB' },
    { id: 13, name: 'Teal', display_name: 'Teal', slug: 'teal', hex: '#0D9488' },
  ];
  get masterColors(): MasterItem[] {
    const apiColors = this.masters?.color || this.masters?.['color'] || [];
    return apiColors.length > 0 ? apiColors : this.fallbackColors;
  }
  get masterThemes(): MasterItem[] { return this.masters?.['wallpaper-theme'] || []; }

  private loadMasters() {
    this.wallpaperService.getMasters().subscribe({
      next: (res: any) => {
        this.masters = res?.data ?? res ?? {};
        const subCats: any[] = this.masters?.['sub-category'] || [];
        this.subCategories = subCats.filter(sc => {
          if (!this.categoryId) return true;
          return sc.category_id === this.categoryId || !sc.category_id;
        });
      }
    });
  }

  // ── Load category metadata and resolve real API category ID ──
  private loadCategoryData() {
    this.wallpaperService.getCategories().subscribe({
      next: (res: any) => {
        const list: any[] = res?.data ?? res ?? [];
        this.allCategories = list.map((c: any) => {
          const constMatch = CATEGORIES.find(cat => cat.id === c.id);
          return { ...c, display_name: c.display_name || (constMatch ? constMatch.display_name : c.name) };
        });
        // Match by slug first, then by ID
        this.category = list.find(c => c.slug === this.categorySlug)
          || list.find(c => String(c.id) === String(this.categoryId))
          || null;
        this.bannerImage = this.getBannerImage();
        // Pre-select current category in filter
        if (this.category) {
          this.selectedCategoryIds = new Set([this.category.id]);
          this.state.updateContext({
            selected_category: this.category.display_name || this.category.name,
            selected_category_id: this.category.id,
            selected_space: null,
            selected_space_id: null,
            entry_point: 'category'
          });
        }
        // Use the real API category ID for search
        if (this.category && this.category.id !== this.categoryId) {
          this.categoryId = this.category.id;
          this.loadWallpapers();
        }
        // Re-filter sub-categories for this category
        this.loadMasters();
      }
    });
  }

  private getBannerImage(): string {
    if (this.category?.image) return this.category.image;
    const slug = (this.category?.slug || this.categorySlug || '').toLowerCase();
    const keys = Object.keys(this.categoryImages);
    if (this.categoryImages[slug]) return this.categoryImages[slug];
    const partial = keys.find(k => slug.includes(k) || k.includes(slug));
    return partial ? this.categoryImages[partial] : this.categoryImages['modern'];
  }

  get categoryName(): string {
    return this.category?.display_name || this.category?.name || this.categorySlug || 'Wallpapers';
  }

  get categoryDescription(): string {
    return this.category?.description || '';
  }

  // ── Load sub-categories from masters ──
  private loadSubCategories() {
    this.wallpaperService.getMasters().subscribe({
      next: (res: any) => {
        const masters = res?.data ?? res;
        const subCats: any[] = masters?.['sub-category'] || [];
        // Filter sub-categories that belong to this category (match by category_id or show all)
        this.subCategories = subCats.filter(sc => {
          if (!this.categoryId) return true;
          return sc.category_id === this.categoryId || !sc.category_id;
        });
      }
    });
  }

  selectSubCategory(subCat: any) {
    // Navigate to listing with sub-category filter or reload with theme
    this.router.navigate(['/listing'], {
      queryParams: {
        category: categorySlugById(this.categoryId) || this.categoryId,
        searchtext: subCat.display_name || subCat.name
      }
    });
  }

  getWallpaperImage(wp: any): string {

    const fallbackImages = [
      wp.thumbnail,
      wp.space1,
      wp.space2,
      wp.space3,
      wp.space4
    ].filter(img => !!img);

    if (fallbackImages.length > 0) {
      const randomIndex = Math.floor(Math.random() * fallbackImages.length);
      return fallbackImages[randomIndex];
    }

    return wp.list_thumbnail;
  }

  // ── Load wallpapers ──
  private loadStartTime = 0;

  loadWallpapers() {
    this.isLoading = true;
    this.loadError = false;
    this.loadStartTime = performance.now();

    const { sortby, sortdir } = this.getSortParams();
    const category = this.selectedCategoryIds.size > 0
      ? [...this.selectedCategoryIds].join(',')
      : (this.categoryId ? String(this.categoryId) : undefined);
    const type = this.selectedTypes.size > 0 ? [...this.selectedTypes].join(',') : undefined;
    const space = this.selectedSpaceIds.size > 0 ? [...this.selectedSpaceIds].join(',') : undefined;
    const theme = this.selectedThemeIds.size > 0 ? [...this.selectedThemeIds].join(',') : undefined;
    const colorNames = [...this.selectedColorIds]
      .map(id => this.masterColors.find(c => c.id === id)?.name)
      .filter(Boolean);
    const color = colorNames.length > 0 ? colorNames.join(',') : undefined;
    const quality = this.selectedQualities.size > 0 ? [...this.selectedQualities].join(',') : undefined;

    this.wallpaperService.searchWallpapers({
      category,
      type,
      space,
      theme,
      color,
      quality,
      vastu: this.vastu ? 'yes' : undefined,
      sortby,
      sortdir,
      page: this.currentPage,
      images: this.pageSize,
    }).subscribe({
      next: (res) => {
        this.wallpapers = res.results || [];

        this.wallpapers = this.wallpapers?.map(wp => ({
          ...wp,
          display_image: this.getWallpaperImage(wp)
        }));
        
        this.totalRecords = res.total ?? 0;
        this.isLoading = false;
        this.dataLayer.setPageTitle(this.categoryName + ' Wallpapers');
        this.pushViewItemList();
        // Build banner images from first page results (use space mockups + list thumbnails)
        if (this.bannerImages.length === 0 && this.wallpapers.length > 0) {
          const imgs: string[] = [];
          for (const wp of this.wallpapers) {
            if (wp.space1) imgs.push(wp.space1);
            else if (wp.list_thumbnail) imgs.push(wp.list_thumbnail);
            if (imgs.length >= 5) break;
          }
          if (imgs.length > 0) this.bannerImages = imgs;
        }
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
      }
    });
  }

  private pushViewItemList(): void {
    const filters: string[] = [];
    if (this.categoryId) filters.push('category:' + this.categoryName);
    this.selectedSpaceIds.forEach(id => filters.push('space:' + id));
    this.selectedColorIds.forEach(id => {
      const c = this.masterColors.find(x => x.id === id);
      filters.push('color:' + (c?.name || id));
    });
    this.selectedThemeIds.forEach(id => filters.push('theme:' + id));
    this.selectedTypes.forEach(t => filters.push('type:' + t));
    this.selectedQualities.forEach(q => filters.push('quality:' + q));
    if (this.vastu) filters.push('vastu:yes');

    this.dataLayer.pushViewItemList({
      listName: this.categoryName + ' Wallpapers',
      listId: 'category_' + (this.categorySlug || ''),
      items: this.wallpapers,
      filters,
      sortType: this.sortBy,
      totalResults: this.totalRecords,
      categoryName: this.categoryName,
      category: this.categoryName,
      timeToLoad: Math.round((performance.now() - this.loadStartTime) / 10) / 100,
    });
  }

  private getSortParams(): { sortby: string; sortdir: string } {
    if (this.sortBy === 'Price: Low \u2192 High') return { sortby: 'price', sortdir: 'asc' };
    if (this.sortBy === 'Price: High \u2192 Low') return { sortby: 'price', sortdir: 'desc' };
    if (this.sortBy === 'Popular') return { sortby: 'updated_at', sortdir: 'desc' };
    if (this.sortBy === 'Newest') return { sortby: 'updated_at', sortdir: 'desc' };
    return { sortby: 'recommended', sortdir: 'desc' };
  }

  onSortChange(opt: string) {
    const prev = this.sortBy;
    this.sortBy = opt;
    this.showSortDropdown = false;
    this.dataLayer.pushSortChange({ sortType: opt, previousSort: prev, totalResults: this.totalRecords });
    this.currentPage = 1;
    this.loadWallpapers();
  }

  private toggleIdSet(set: Set<number>, id: number): Set<number> {
    const s = new Set(set);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  }

  private toggleStringSet(set: Set<string>, val: string): Set<string> {
    const s = new Set(set);
    s.has(val) ? s.delete(val) : s.add(val);
    return s;
  }

  toggleType(type: string) { const was = this.selectedTypes.has(type); this.selectedTypes = this.toggleStringSet(this.selectedTypes, type); this.pushFilterEvt('type', type, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleCategory(id: number) { const was = this.selectedCategoryIds.has(id); const n = this.allCategories.find(c => c.id === id)?.name || String(id); this.selectedCategoryIds = this.toggleIdSet(this.selectedCategoryIds, id); this.pushFilterEvt('category', n, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleSpace(id: number) { const was = this.selectedSpaceIds.has(id); this.selectedSpaceIds = this.toggleIdSet(this.selectedSpaceIds, id); this.pushFilterEvt('space', String(id), was); this.currentPage = 1; this.loadWallpapers(); }
  toggleColor(id: number) { const was = this.selectedColorIds.has(id); const n = this.masterColors.find(c => c.id === id)?.name || String(id); this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, id); this.pushFilterEvt('color', n, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleTheme(id: number) { const was = this.selectedThemeIds.has(id); this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, id); this.pushFilterEvt('theme', String(id), was); this.currentPage = 1; this.loadWallpapers(); }
  toggleQuality(q: string) { const was = this.selectedQualities.has(q); this.selectedQualities = this.toggleStringSet(this.selectedQualities, q); this.pushFilterEvt('quality', q, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleVastu() { this.vastu = !this.vastu; this.pushFilterEvt('vastu', 'yes', !this.vastu); this.currentPage = 1; this.loadWallpapers(); }

  private get totalActiveFilterCount(): number {
    return this.selectedCategoryIds.size + this.selectedSpaceIds.size + this.selectedColorIds.size
      + this.selectedThemeIds.size + this.selectedTypes.size + this.selectedQualities.size + (this.vastu ? 1 : 0);
  }
  private buildActiveFilters(): string[] {
    const f: string[] = [];
    this.selectedCategoryIds.forEach(id => f.push('category:' + (this.allCategories.find(c => c.id === id)?.name || id)));
    this.selectedSpaceIds.forEach(id => f.push('space:' + id));
    this.selectedColorIds.forEach(id => f.push('color:' + (this.masterColors.find(c => c.id === id)?.name || id)));
    this.selectedThemeIds.forEach(id => f.push('theme:' + id));
    this.selectedTypes.forEach(t => f.push('type:' + t));
    this.selectedQualities.forEach(q => f.push('quality:' + q));
    if (this.vastu) f.push('vastu:yes');
    return f;
  }
  private getActiveFilterValues(type: string): string {
    switch (type) {
      case 'category': return [...this.selectedCategoryIds].map(id => this.allCategories.find(c => c.id === id)?.name || id).join(', ');
      case 'space': return [...this.selectedSpaceIds].join(', ');
      case 'color': return [...this.selectedColorIds].map(id => this.masterColors.find(c => c.id === id)?.name || id).join(', ');
      case 'theme': return [...this.selectedThemeIds].join(', ');
      case 'type': return [...this.selectedTypes].join(', ');
      case 'quality': return [...this.selectedQualities].join(', ');
      case 'vastu': return this.vastu ? 'yes' : '';
      default: return '';
    }
  }
  private pushFilterEvt(type: string, value: string, wasActive: boolean): void {
    const allValues = this.getActiveFilterValues(type);
    if (wasActive) {
      this.dataLayer.pushFilterRemove({ filterType: type, filterValue: allValues || value, filtersActive: this.buildActiveFilters(), totalResults: this.totalRecords });
    } else {
      this.dataLayer.pushFilterApply({ filterType: type, filterValue: allValues || value, totalActiveFilters: this.totalActiveFilterCount, totalResults: this.totalRecords });
    }
  }

  clearFilters() {
    const cleared = this.buildActiveFilters();
    this.selectedTypes = new Set();
    this.selectedCategoryIds = this.category ? new Set([this.category.id]) : new Set();
    this.selectedSpaceIds = new Set();
    this.selectedColorIds = new Set();
    this.selectedThemeIds = new Set();
    this.selectedQualities = new Set();
    this.vastu = false;
    this.sortBy = 'Recommended';
    this.currentPage = 1;
    if (cleared.length > 0) this.dataLayer.pushFilterClearAll({ filtersCleared: cleared, totalResultsAfter: this.totalRecords });
    this.loadWallpapers();
  }

  get hasActiveFilters(): boolean {
    return this.selectedTypes.size > 0 || this.selectedSpaceIds.size > 0 ||
      this.selectedColorIds.size > 0 || this.selectedThemeIds.size > 0 ||
      this.selectedQualities.size > 0 || this.vastu ||
      (this.selectedCategoryIds.size > 1 || (this.selectedCategoryIds.size === 1 && !this.category));
  }

  get activeFilterChips(): { label: string; type: string; value: string; id?: number }[] {
    const chips: { label: string; type: string; value: string; id?: number }[] = [];
    this.selectedCategoryIds.forEach(id => {
      if (this.category && id === this.category.id) return;
      const cat = this.allCategories.find(c => c.id === id);
      chips.push({ label: cat?.display_name || cat?.name || 'Category', type: 'category', value: String(id), id });
    });
    this.selectedSpaceIds.forEach(id => {
      const sp = this.masterSpaces.find(s => s.id === id);
      chips.push({ label: sp?.display_name || sp?.name || 'Space', type: 'space', value: String(id), id });
    });
    this.selectedTypes.forEach(t => chips.push({ label: t, type: 'type', value: t }));
    this.selectedQualities.forEach(q => chips.push({ label: q, type: 'quality', value: q }));
    this.selectedColorIds.forEach(id => {
      const c = this.masterColors.find(x => x.id === id);
      chips.push({ label: c?.name || 'Color', type: 'color', value: String(id), id });
    });
    this.selectedThemeIds.forEach(id => {
      const t = this.masterThemes.find(x => x.id === id);
      chips.push({ label: t?.name || 'Theme', type: 'theme', value: String(id), id });
    });
    if (this.vastu) chips.push({ label: 'Vastu Friendly', type: 'vastu', value: 'yes' });
    return chips;
  }

  removeChip(chip: { type: string; value: string; id?: number }) {
    switch (chip.type) {
      case 'category': this.selectedCategoryIds = this.toggleIdSet(this.selectedCategoryIds, chip.id || +chip.value); break;
      case 'space': this.selectedSpaceIds = this.toggleIdSet(this.selectedSpaceIds, chip.id || +chip.value); break;
      case 'color': this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, chip.id || +chip.value); break;
      case 'theme': this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, chip.id || +chip.value); break;
      case 'type': this.selectedTypes = this.toggleStringSet(this.selectedTypes, chip.value); break;
      case 'quality': this.selectedQualities = this.toggleStringSet(this.selectedQualities, chip.value); break;
      case 'vastu': this.vastu = false; break;
    }
    this.currentPage = 1;
    this.loadWallpapers();
  }

  // ── Pagination ──
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }
  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);
    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) pages.push(i);
    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    const action = p === this.currentPage + 1 ? 'next' : p === this.currentPage - 1 ? 'prev' : 'page_number';
    this.currentPage = p;
    this.dataLayer.pushPaginationClick({ action, pageNumber: p, totalResults: this.totalRecords, itemsLoaded: p * this.pageSize });
    this.userActionCount++;
    this.router.navigate([], { relativeTo: this.route, queryParams: { page: p > 1 ? p : null }, queryParamsHandling: 'merge', replaceUrl: false });
    this.loadWallpapers();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Navigation ──
  navigateToPDP(wp: SearchWallpaper) {
    const idx = this.wallpapers.indexOf(wp) + 1;
    this.dataLayer.pushSelectItem({
      listName: this.categoryName + ' Wallpapers',
      listId: 'category_' + (this.categorySlug || ''),
      listType: 'category',
      source: 'plp',
      item: wp, index: idx,
      categoryFallback: this.categoryName,
    });
    this.router.navigateByUrl(buildPdpUrl(wp));
  }

  navigateToSimilar(wp: SearchWallpaper, e: Event) {
    e.stopPropagation();
    const width = 1100, height = 700;
    const left = (screen.width - width) / 2, top = (screen.height - height) / 2;
    const noise = new Set(['wallpaper', 'pattern', 'design', 'digital', 'print', 'the', 'and', 'with', 'for', 'of', 'in', 'on', 'a', 'an']);
    const keywords = (wp.name || '').split(/[\s\-_]+/).filter(w => w.length > 2 && !noise.has(w.toLowerCase())).slice(0, 5).join(' ');
    const url = `/similar/${wp.slug || wp.id}?name=${encodeURIComponent(wp.name)}&keywords=${encodeURIComponent(keywords)}&collection=${wp.collection || ''}&category=${wp.collection || ''}`;
    window.open(url, '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }

  // Solr can return `quality` either as a string ("exclusive") or a single-element array (["exclusive"]).
  // Solr can return `quality` as a string ("exclusive") or a single-element array.
  private hasQuality(wp: any, quality: string): boolean {
    const q = wp?.quality;
    if (!q) return false;
    if (Array.isArray(q)) return q.some((v: any) => String(v).toLowerCase() === quality);
    return String(q).toLowerCase() === quality;
  }

  isExclusive(wp: any): boolean { return this.hasQuality(wp, 'exclusive'); }
  isPremium(wp: any): boolean { return this.hasQuality(wp, 'premium'); }

  // ── Wishlist ──
  isWishlisted(id: string): boolean { return this.wishlistIds.has(id); }

  toggleWishlistItem(wp: any, e: Event) {
    e.stopPropagation();
    const id = wp.id.toString();
    const isAdding = !this.wishlistIds.has(id);
    this.state.toggleWishlist(id, {
      id, name: wp.name, image: wp.list_thumbnail, price: wp.price,
      wallpaper_type: wp.wallpaper_type || ''
    });
    if (isAdding) {
      this.dataLayer.pushAddToWishlist({ item: wp, source: 'plp', categoryFallback: this.categoryName });
    }
    this.userActionCount++;
  }
}
